import { useRef, useState } from "react"
import { createFileRoute } from "@tanstack/react-router"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { auth } from "@/lib/auth"
import { useT } from "@/lib/i18n"
import { Badge, Button, Card, Input, Sheet, Spinner, Stub, Toast } from "@/components/ui"
import { formatBytes, timeAgo } from "@/lib/utils"
import type { KnowledgeCollectionOut } from "@/lib/types"
import { PlusIcon, Trash2Icon, UploadIcon, FolderOpenIcon } from "lucide-react"

export const Route = createFileRoute("/_app/knowledge")({
  component: KnowledgePage,
})

function KnowledgePage() {
  const t = useT()
  const me = auth.me()
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ["knowledge", me?.id],
    queryFn: () => api<KnowledgeCollectionOut[]>("/api/knowledge/collections", {
      query: { user_id: me?.id },
    }),
    enabled: !!me?.id,
  })
  const [creating, setCreating] = useState(false)

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">{t("know.title")}</h1>
          <p className="text-[13px] text-muted-foreground mt-1.5">
            {t("know.sub")}
          </p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <PlusIcon className="h-4 w-4" /> {t("know.new")}
        </Button>
      </div>

      {list.isLoading && <div className="flex justify-center py-8"><Spinner /></div>}
      {list.error && <Toast variant="destructive">{(list.error as Error).message}</Toast>}
      {list.data?.length === 0 && (
        <Stub
          title={t("know.empty")}
          hint={t("know.emptyHint")}
        />
      )}

      <div className="grid gap-3.5 sm:grid-cols-2">
        {list.data?.map((c) => <CollectionRow key={c.id} c={c} />)}
      </div>

      <CreateCollectionSheet open={creating} onClose={() => { setCreating(false); qc.invalidateQueries({ queryKey: ["knowledge"] }) }} />
    </div>
  )
}

function CollectionRow({ c }: { c: KnowledgeCollectionOut }) {
  const t = useT()
  const me = auth.me()
  const qc = useQueryClient()
  const file = useRef<HTMLInputElement | null>(null)
  const [uploading, setUploading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  const del = useMutation({
    mutationFn: () => api(
      `/api/knowledge/collections/${c.id}`,
      { method: "DELETE", query: { user_id: me?.id } },
    ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["knowledge"] }),
  })

  async function onPick(files: FileList | null) {
    if (!files || !files.length || !me?.id) return
    setUploading(true)
    setErr(null)
    try {
      // Multipart upload; the server extracts text (PDF / Word / PowerPoint /
      // Excel via the document parsers, code and prose as UTF-8), then
      // chunks + embeds in the same request.
      const fd = new FormData()
      for (const f of Array.from(files)) fd.append("files", f, f.name)
      await api(
        `/api/knowledge/collections/${c.id}/upload`,
        { method: "POST", rawBody: fd, query: { user_id: me.id } },
      )
      qc.invalidateQueries({ queryKey: ["knowledge"] })
    } catch (e) {
      setErr((e as Error).message)
    } finally {
      setUploading(false)
    }
  }

  const statusKey = `know.status.${c.status}`
  const statusLabel = t(statusKey) === statusKey ? c.status : t(statusKey)

  return (
    <Card className="flex flex-col gap-4 p-[18px] transition hover:border-primary/20">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-primary/20 bg-primary/[0.12]">
          <FolderOpenIcon className="h-5 w-5 text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-semibold text-foreground">{c.name}</h3>
          <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11.5px] text-muted-foreground">
            <Badge variant={c.status === "ready" ? "ok" : c.status === "error" ? "destructive" : "warn"}>{statusLabel}</Badge>
            <span>{t("know.chunks", { n: c.chunk_count ?? 0 })} · {formatBytes(c.size_bytes ?? 0)} · {timeAgo(c.created_at)}</span>
          </div>
        </div>
      </div>
      {err && <Toast variant="destructive">{err}</Toast>}
      <div className="flex items-center gap-2">
        <input
          ref={file}
          type="file"
          multiple
          accept=".txt,.md,.markdown,.csv,.json,.log,text/*"
          hidden
          onChange={(e) => onPick(e.target.files)}
        />
        <Button variant="outline" size="sm" onClick={() => file.current?.click()} disabled={uploading}>
          {uploading ? <Spinner /> : <UploadIcon className="h-3.5 w-3.5" />} {t("know.upload")}
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={t("know.delete")} onClick={() => {
          if (window.confirm(t("know.deleteConfirm", { name: c.name }))) del.mutate()
        }}>
          <Trash2Icon className="h-4 w-4 text-muted-foreground hover:text-destructive" />
        </Button>
      </div>
    </Card>
  )
}

type SourceType = "local" | "gdrive" | "gmail" | "notion" | "atlassian" | "dropbox" | "onedrive" | "box"

/** Labels, hints and field labels live in i18n: know.source.<value>, know.hint.<value>, know.field.<value>. */
const SOURCES: { value: SourceType; field?: { key: string; placeholder: string; list?: boolean } }[] = [
  { value: "local" },
  { value: "gdrive", field: { key: "folder_id", placeholder: "1AbC…" } },
  { value: "gmail", field: { key: "query", placeholder: "label:projects newer_than:1y" } },
  { value: "notion" },
  { value: "atlassian", field: { key: "space_keys", placeholder: "ENG, DOCS", list: true } },
  { value: "dropbox", field: { key: "path", placeholder: "/Documents" } },
  { value: "onedrive", field: { key: "folder_id", placeholder: "01ABC…" } },
  { value: "box", field: { key: "folder_id", placeholder: "0" } },
]

function CreateCollectionSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const t = useT()
  const me = auth.me()
  const [name, setName] = useState("")
  const [source, setSource] = useState<SourceType>("local")
  const [cfgValue, setCfgValue] = useState("")
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const spec = SOURCES.find((s) => s.value === source)!
  async function go() {
    if (!me?.id) return
    setBusy(true); setErr(null)
    const source_config: Record<string, unknown> = {}
    if (spec.field && cfgValue.trim()) {
      source_config[spec.field.key] = spec.field.list
        ? cfgValue.split(",").map((x) => x.trim()).filter(Boolean)
        : cfgValue.trim()
    }
    try {
      const created = await api<{ id: string }>("/api/knowledge/collections", {
        method: "POST",
        body: { user_id: me.id, name, source_type: source, source_config },
      })
      if (source !== "local") {
        // Cloud sources index in the background right away.
        await api(`/api/knowledge/collections/${created.id}/index`, { method: "POST", query: { user_id: me.id } })
      }
      setName(""); setCfgValue(""); setSource("local")
      onClose()
    } catch (e) {
      setErr((e as { detail?: string }).detail || (e as Error).message)
    } finally {
      setBusy(false)
    }
  }
  return (
    <Sheet
      open={open}
      onClose={onClose}
      title={t("know.new")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t("know.cancel")}</Button>
          <Button onClick={go} disabled={busy || !name.trim()}>{busy ? <Spinner /> : null}{t("know.create")}</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("know.name")}</label>
          <Input value={name} onChange={(e) => setName(e.target.value)} autoFocus placeholder={t("know.namePh")} />
        </div>
        <div className="space-y-1.5">
          <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t("know.source")}</label>
          <select
            value={source}
            onChange={(e) => { setSource(e.target.value as SourceType); setCfgValue("") }}
            className="w-full rounded-xl border border-border bg-black/25 px-3.5 py-2.5 text-sm text-foreground [&>option]:bg-card"
          >
            {SOURCES.map((s) => <option key={s.value} value={s.value}>{t(`know.source.${s.value}`)}</option>)}
          </select>
          <p className="text-[11px] text-muted-foreground">{t(`know.hint.${source}`)}</p>
        </div>
        {spec.field && (
          <div className="space-y-1.5">
            <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{t(`know.field.${source}`)}</label>
            <Input value={cfgValue} onChange={(e) => setCfgValue(e.target.value)} placeholder={spec.field.placeholder} className="font-mono" />
          </div>
        )}
        {err && <Toast variant="destructive">{err}</Toast>}
      </div>
    </Sheet>
  )
}
